import { camelCase, sortBy, upperFirst } from 'lodash-es';
import { getImg } from '@home/_config/getImgs';
import type { RankDataType } from '@/pages/dashboard/components/MetricsCardRank';
import {
  buildLiveMetricTrendCurrentSeries,
  // buildLiveMetricTrendPreviousYearSeries,
  type ILiveMetricTrendData,
} from '@sg/pages/StudioManagement/LiveTitles/utils/liveMetricTrendChart';
import { resolveLiveTitleChannelColor } from '@sg/pages/StudioManagement/LiveTitles/utils/channelColors';
import type { IGggFinanceChannel, IGggFinanceTimelinePoint } from '../../../types';

// Default channel rows when API returns an empty channels array (by game_title).
// 有个问题 就是 如果后端game title 名字 改了， 这边也要改， 但是也没有办法 因为没有unique key
const FINANCE_DEFAULT_CHANNELS_BY_GAME: Record<string, string[]> = {
  'Path of Exile': ['Official Website', 'Steam', 'Kakao', 'Epic', 'Hotcool'],
  'Path of Exile 2': ['Steam', 'Official Website', 'Playstation', 'Xbox', 'Kakao'],
  'POE1国服': ['WeGame'],
  'POE2国服': ['WeGame'],
};

export type TFinanceMetricTipKey = 'revenue_after_refund' | 'mau' | 'arpu';

// Metric data-source tips keyed by game_title (same keys as channel defaults).
const FINANCE_METRIC_TIPS_BY_GAME: Record<
  string,
  Partial<Record<TFinanceMetricTipKey, string>>
> = {
  'Path of Exile 2': {
    revenue_after_refund:
      'Revenue after Refund is sourced from DataBrain (excl. Kakoo + Hotcool) + OM Excels for Kakao and Hotcool.\n'
      + 'Base Game Units after Refund is sourced from DataBrain',
    mau: 'MAU is sourced from DataBrain + OM Excels for Hotcool.',
    arpu: 'ARPU is calculated as (Total Revenue after Refund) / (Total MAU).',
  },
  'Path of Exile': {
    revenue_after_refund:
      'Revenue after Refund is sourced from DataBrain (excl. Kakoo + Hotcool) + OM Excels for Kakao and Hotcool.',
    mau: 'MAU is sourced from DataBrain + OM Excels for Hotcool.',
    arpu: 'ARPU is calculated as (Total Revenue after Refund) / (Total MAU).',
  },
  'POE2国服': {
    revenue_after_refund:
      'Revenue after Refund is sourced from IMC, provided by the Finance Team.',
    mau: 'MAU is sourced from DataBrain.',
    arpu: 'ARPU is sourced from DataBrain.',
  },
  'POE1国服': {
    revenue_after_refund:
      'Revenue after Refund is sourced from IMC, provided by the Finance Team.',
    mau: 'MAU is sourced from DataBrain.',
    arpu: 'ARPU is sourced from DataBrain.',
  },
};

export const getFinanceMetricTip = (
  gameTitle?: string,
  metricKey?: TFinanceMetricTipKey,
): string | undefined => {
  if (!gameTitle || !metricKey) {
    return undefined;
  }
  return FINANCE_METRIC_TIPS_BY_GAME[gameTitle]?.[metricKey];
};

export type IFinanceChannelRankItem = Omit<RankDataType, 'value'> & {
  value: RankDataType['value'] | null;
  isPlaceholder?: boolean;
};

export const formatMoM = (mom?: number | string | null): string => {
  if (mom === null || mom === undefined || mom === '') {
    return '';
  }
  const num = Number(mom);
  if (Number.isNaN(num)) {
    return '';
  }
  return `${num.toFixed(2)}%`;
};

export const buildTrendData = (
  timeline?: IGggFinanceTimelinePoint[],
  metricName = 'value',
): ILiveMetricTrendData | undefined => {
  if (!timeline?.length) {
    return undefined;
  }

  const xAxisData = timeline.map(point => point.month || '');
  const currentData = timeline.map(point => point.value ?? null);
  // Backend returns previous_year, but finance charts only show current period for now.
  // const hasPreviousYear = timeline.some(
  //   point => point.previous_year !== null && point.previous_year !== undefined,
  // );

  const series: any[] = [
    buildLiveMetricTrendCurrentSeries('This Month', currentData),
  ];

  // if (hasPreviousYear) {
  //   series.push(
  //     buildLiveMetricTrendPreviousYearSeries(
  //       'Same Month Last Year',
  //       timeline.map(point => point.previous_year ?? null),
  //     ),
  //   );
  // }

  return {
    xAxisData,
    series,
    metrics_name: metricName,
  };
};

export const formatPayerRatio = (value?: number | null): string => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const resolveChannelShare = (
  channel: IGggFinanceChannel,
  totalValue: number,
): number => {
  if (channel?.pct === null || channel?.pct === undefined) {
    return totalValue > 0 ? (Number(channel.value) || 0) / totalValue : 0;
  }
  const pct = Number(channel.pct);
  if (Number.isNaN(pct)) {
    return 0;
  }
  // API may return either ratio (0.445) or percentage (44.5).
  return pct > 1 ? pct / 100 : pct;
};

const buildPlaceholderChannelRankData = (
  gameTitle?: string,
): IFinanceChannelRankItem[] => {
  const defaultChannelNames = FINANCE_DEFAULT_CHANNELS_BY_GAME[gameTitle || ''];
  if (!defaultChannelNames?.length) {
    return [];
  }

  return defaultChannelNames.map((channelName, index) => ({
    nameEn: channelName,
    value: null,
    subValue: 0,
    icon: getImg(upperFirst(camelCase(channelName))),
    color: resolveLiveTitleChannelColor(channelName),
    link: '',
    isPlaceholder: true,
  }));
};

export const buildFinanceChannelRankData = (
  channels?: IGggFinanceChannel[],
  gameTitle?: string,
): IFinanceChannelRankItem[] => {
  if (!channels?.length) {
    return buildPlaceholderChannelRankData(gameTitle);
  }

  const totalValue = channels.reduce(
    (sum, item) => sum + (Number(item?.value) || 0),
    0,
  );

  return sortBy(channels, ['value'])
    .reverse()
    .slice(0, 5)
    .map((channel, index) => {
      const channelName = channel?.name || '';
      const value = Number(channel?.value) || 0;

      return {
        nameEn: channelName,
        value,
        subValue: resolveChannelShare(channel, totalValue),
        icon: getImg(upperFirst(camelCase(channelName))),
        color: resolveLiveTitleChannelColor(channelName),
        link: '',
      };
    });
};
